/** Root columns on leadsrecord ↔ keys inside `fields` JSON. */
export const LEAD_ROOT_SYNC_KEYS = [
  'lead_source',
  'lead_type',
  'lead_status',
  'contact_name',
  'contact_email',
  'contact_phone',
  'company_name',
  'country_code',
] as const;

const FIELD_NAME_TO_ROOT: Record<string, string> = {
  leadsource: 'lead_source',
  leadtype: 'lead_type',
  leadstatus: 'lead_status',
  source: 'lead_source',
  contactname: 'contact_name',
  contactemail: 'contact_email',
  contactphone: 'contact_phone',
  companyname: 'company_name',
  organisation: 'company_name',
  organization: 'company_name',
  countrycode: 'country_code',
};

function normKey(s: string): string {
  return (s || '').toLowerCase().replace(/[\s_-]+/g, '');
}

export function parseLeadFields(lead: any): Record<string, any> {
  if (!lead) {
    return {};
  }
  if (typeof lead.fields === 'string') {
    try {
      lead.fields = JSON.parse(lead.fields);
    } catch {
      lead.fields = {};
    }
  }
  if (!lead.fields || typeof lead.fields !== 'object' || Array.isArray(lead.fields)) {
    lead.fields = {};
  }
  return lead.fields;
}

/** Keep root columns and `fields` object in sync for the profile form. */
/** Best available string for a root column (root, fields JSON, custom field names). */
export function resolveRootDisplayValue(
  lead: any,
  rootKey: string,
  allLeadFields: any[] = []
): string {
  if (!lead) {
    return '';
  }
  syncRootAndCustomFields(lead);
  const fields = parseLeadFields(lead);
  const candidates: unknown[] = [lead[rootKey], fields[rootKey]];

  for (const item of allLeadFields || []) {
    if (!item?.field_name) {
      continue;
    }
    if (FIELD_NAME_TO_ROOT[normKey(item.field_name)] === rootKey) {
      candidates.push(fields[item.field_name]);
    }
  }

  for (const [fn, val] of Object.entries(fields)) {
    if (FIELD_NAME_TO_ROOT[normKey(fn)] === rootKey) {
      candidates.push(val);
    }
  }

  for (const c of candidates) {
    if (c != null && c !== '') {
      return String(c).trim();
    }
  }
  return '';
}

export function syncRootAndCustomFields(lead: any): void {
  if (!lead) {
    return;
  }
  const fields = parseLeadFields(lead);

  for (const key of LEAD_ROOT_SYNC_KEYS) {
    const rootVal = lead[key];
    const fieldVal = fields[key];
    if (rootVal != null && rootVal !== '' && (fieldVal == null || fieldVal === '')) {
      fields[key] = rootVal;
    } else if (
      (rootVal == null || rootVal === '') &&
      fieldVal != null &&
      fieldVal !== ''
    ) {
      lead[key] = fieldVal;
    }
  }

  for (const [fn, val] of Object.entries(fields)) {
    const rootKey = FIELD_NAME_TO_ROOT[normKey(fn)];
    if (!rootKey) {
      continue;
    }
    if (val != null && val !== '' && (lead[rootKey] == null || lead[rootKey] === '')) {
      lead[rootKey] = val;
    } else if (
      lead[rootKey] != null &&
      lead[rootKey] !== '' &&
      (val == null || val === '')
    ) {
      fields[fn] = lead[rootKey];
    }
  }
}

/** HTML date input expects yyyy-MM-dd. */
export function toDateInputValue(value: unknown): string {
  if (value == null || value === '') {
    return '';
  }
  if (typeof value === 'string') {
    const s = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      return s;
    }
    if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) {
      return s.slice(0, 10);
    }
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      return d.toISOString().slice(0, 10);
    }
    return s;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  return String(value);
}

function ensureSubFieldRows(lead: any, field: any): void {
  if (!field?.sub_fields?.length) {
    return;
  }
  const fields = parseLeadFields(lead);
  const subKey = field.field_name + '|subf';
  if (!Array.isArray(fields[subKey]) || !fields[subKey].length) {
    const row: Record<string, unknown> = {};
    field.sub_fields.forEach((sub: any, idx: number) => {
      const cellKey = sub.field_name + '|' + idx;
      row[cellKey] = fields[sub.field_name] ?? '';
    });
    fields[subKey] = [row];
  }
}

export function commitResolvedRootValues(lead: any, allLeadFields: any[] = []): void {
  if (!lead) {
    return;
  }
  const fields = parseLeadFields(lead);
  for (const key of LEAD_ROOT_SYNC_KEYS) {
    const resolved = resolveRootDisplayValue(lead, key, allLeadFields);
    if (resolved !== '') {
      lead[key] = resolved;
      fields[key] = resolved;
    }
  }
}

/** Deep-clone a table row into a plain lead object for editing. */
export function cloneLeadForEdit(row: any): any {
  if (!row) {
    return {};
  }
  let plain: any = row;
  if (typeof row.get === 'function') {
    try {
      plain = row.get({ plain: true });
    } catch {
      plain = row;
    }
  } else if (row.dataValues && typeof row.dataValues === 'object') {
    plain = { ...row.dataValues, ...row };
  }
  try {
    return JSON.parse(JSON.stringify(plain));
  } catch {
    return { ...plain };
  }
}

export function normalizeLeadEditModel(lead: any, allLeadFields: any[] = []): void {
  if (!lead) {
    return;
  }
  syncRootAndCustomFields(lead);
  commitResolvedRootValues(lead, allLeadFields);
  const fields = parseLeadFields(lead);

  for (const item of allLeadFields || []) {
    if (!item?.field_name) {
      continue;
    }
    if (item.type === 'date') {
      const raw = resolveRawFieldValue(lead, item.field_name);
      if (raw != null && raw !== '') {
        fields[item.field_name] = toDateInputValue(raw);
      }
    }
    ensureSubFieldRows(lead, item);
  }
}

export function resolveRawFieldValue(lead: any, fieldName: string): unknown {
  if (!lead) {
    return '';
  }
  const fields = parseLeadFields(lead);
  let v = fields[fieldName];
  if ((v == null || v === '') && lead[fieldName] != null && lead[fieldName] !== '') {
    v = lead[fieldName];
  }
  const rootKey = FIELD_NAME_TO_ROOT[normKey(fieldName)];
  if ((v == null || v === '') && rootKey && lead[rootKey] != null && lead[rootKey] !== '') {
    v = lead[rootKey];
  }
  return v ?? '';
}

export function resolveFieldDisplayValue(lead: any, item: { field_name: string; type?: string }): unknown {
  const raw = resolveRawFieldValue(lead, item.field_name);
  if (item.type === 'date') {
    return toDateInputValue(raw);
  }
  return raw ?? '';
}

export function writeFieldValue(lead: any, fieldName: string, value: unknown): void {
  if (!lead) {
    return;
  }
  const fields = parseLeadFields(lead);
  fields[fieldName] = value;
  if ((LEAD_ROOT_SYNC_KEYS as readonly string[]).includes(fieldName)) {
    lead[fieldName] = value;
  }
  const rootKey = FIELD_NAME_TO_ROOT[normKey(fieldName)];
  if (rootKey) {
    lead[rootKey] = value;
    fields[fieldName] = value;
  }
}

export function selectOptionsWithCurrent(
  options: string[],
  current: unknown
): string[] {
  const opts = [...options];
  if (current == null || current === '') {
    return opts;
  }
  const cur = String(current).trim();
  if (!opts.some((o) => String(o).trim() === cur)) {
    opts.unshift(cur);
  }
  return opts;
}

export function compareSelectStrings(a: unknown, b: unknown): boolean {
  return String(a ?? '').trim() === String(b ?? '').trim();
}

export function mergeLeadUpdateResponse(lead: any, response: any): void {
  if (!lead || !response || typeof response !== 'object') {
    return;
  }
  const body =
    typeof response === 'string'
      ? (() => {
          try {
            return JSON.parse(response);
          } catch {
            return null;
          }
        })()
      : response;

  if (!body || typeof body !== 'object') {
    return;
  }

  Object.assign(lead, body);
  if (body.fields != null) {
    lead.fields = body.fields;
  }
}

/** Dynamic fields already rendered as fixed root inputs (avoid empty duplicates). */
export const OVERVIEW_FIXED_FIELD_NAMES = new Set(
  LEAD_ROOT_SYNC_KEYS.map((k) => normKey(k))
);

export function isDuplicateOverviewField(fieldName: string, hasFixedRoot: boolean): boolean {
  if (!hasFixedRoot) {
    return false;
  }
  return OVERVIEW_FIXED_FIELD_NAMES.has(normKey(fieldName));
}

const LEAD_USER_REF_KEYS = ['user', 'updated_by', 'assign_to'] as const;

/** Omit `prefix` when absent so updateLeads payloads do not send `prefix: null`. */
export function sanitizeUserRef(
  user: Record<string, unknown> | null | undefined
): Record<string, unknown> | undefined {
  if (user == null || typeof user !== 'object' || Array.isArray(user)) {
    return undefined;
  }
  const sanitized: Record<string, unknown> = { ...user };
  if (sanitized['prefix'] == null || String(sanitized['prefix']).trim() === '') {
    delete sanitized['prefix'];
  }
  return sanitized;
}

export function sanitizeLeadUpdatePayload(data: unknown): unknown {
  if (data == null || typeof data !== 'object') {
    return data;
  }
  if (Array.isArray(data)) {
    return data.map((item) => sanitizeLeadUpdatePayload(item));
  }
  const payload = { ...(data as Record<string, unknown>) };
  for (const key of LEAD_USER_REF_KEYS) {
    if (key in payload && payload[key] != null) {
      payload[key] = sanitizeUserRef(payload[key] as Record<string, unknown>);
    }
  }
  return payload;
}
